import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { UiKitModule } from '@serviceplan/ui-kit';
import { ClientCoreModule, ClientEnvironment, ClientNotificationHandlerService, ENVIRONMENT, HasRoleDirective, TagsDataConfig } from '@thehubz/client/core';
import { TagsApiService, TagsWithAssetCount } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { Observable, tap } from 'rxjs';
import { TagsChipAutoWidthDirective } from './tags-chip-auto-width.directive';

@Component({
  selector: 'thehubz-tags-chip',
  templateUrl: './tags-chip.component.html',
  styleUrls: ['./tags-chip.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatChipsModule,
    MatInputModule,
    MatIconModule,
    MatDividerModule,
    ClientCoreModule,
    UiKitModule,
    TagsChipAutoWidthDirective,
    HasRoleDirective,
  ],
})
export class TagsChipComponent implements OnInit, OnChanges {
  /** Tag data */
  @Input() tagData!: TagsDataConfig;
  /** Tags Observable*/
  @Input() tagsObs!: Observable<TagsWithAssetCount[]>;
  /** Get the searched text */
  @Input() searchText!: string;
  /** Get the checkbox value */
  @Input() selectDefaultKeywordFlag?: boolean = false;
  /** Paginator object */
  @Input() paginator!: Partial<PageEvent>;
  /** Store tag list from api */
  tagsListWithAssetCount!: TagsWithAssetCount[];
  /** Store filtered tag list from the search input */
  filteredTagsList!: TagsWithAssetCount[];
  /** Store temporary filtered tag list from the search input */
  tempFilteredTagsList: TagsWithAssetCount[] = [];
  /** Store selected tag list */
  selectedTagsList: TagsWithAssetCount[] = [];
  /** Store current editing tag id */
  editingSelectedTag = '';
  /** Toggle the edit tag */
  toggleEditSelectedTag = false;
  /** Bind with edit tag input field */
  tagName = '';
  /** Toggle the edit tag */
  toggleEditTag = false;
  /** Store current editing tag id */
  editingTag = '';
  /** Show tag empty state */
  showEmptyState = false;
  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;

  @Output() updateTagName = new EventEmitter<AnyType>();
  @Output() paginatorEvent = new EventEmitter<{
    selectedTagsList: TagsWithAssetCount[];
    disableApply?: boolean;
    currentlySelectedTagList?: TagsWithAssetCount[];
  }>();
  @Output() searchedTagsEvent = new EventEmitter<{ selectedTagsList: TagsWithAssetCount[]; disableApply?: boolean; selectedTagName?: AnyType }>();

  @ViewChild('renameInput') renameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('renameSearchInput') renameSearchInput?: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private tagsApiService: TagsApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchText']?.currentValue || changes['searchText']?.currentValue === '') {
      this.searchText = changes['searchText']?.currentValue;
      this.searchTextChanged();
    }
    if (changes['selectDefaultKeywordFlag']) {
      this.updateSelectedTagsOnCheckboxChanges();
    }
  }

  ngOnInit(): void {
    // remove tags from filter because it appearing it the filter query when reopening the dialog
    if (this.tagData.filters?.tags) delete this.tagData.filters.tags;
    this.selectedTagsList = this.tagData?.selectedTags || [];
    this.updateTagsList();
    if (this.tagData.callFrom === 'dialog') this.searchedTagsEvent.emit({ selectedTagsList: this.selectedTagsList });
  }

  /**
   * Fetch all tags with asset counts
   */
  fetchTagsWithAssetCount(disableApply?: boolean) {
    if (this.tagData.existingTags) {
      this.tagsListWithAssetCount = this.tagData.existingTags;
      this.showEmptyState = this.tagData.existingTags.length === 0;
      this._showLoader(false);
      this.updateTagAfterAPICall(disableApply);
    } else {
      this.tagsApiService
        .tagsControllerFindAllWithAssetCount(this.tagData.searchText, JSON.stringify(this.tagData.filters))
        .pipe(tap(() => this._showLoader(false)))
        .subscribe({
          next: (response) => {
            this.tagsListWithAssetCount = response;
            this.showEmptyState = response.length === 0;
            this.updateTagAfterAPICall(disableApply);
          },
          error: (err) => {
            this.clientNotificationHandlerService.errorNotification(err);
          },
        });
    }
  }

  updateTagAfterAPICall(disableApply?: boolean) {
    this.filteredTagsList = this.tagsListWithAssetCount;
    if (this.selectedTagsList.length) {
      this.updateSelectedTagsAssetCount();
      // remove selected tags from filteredTagsList
      this.filteredTagsList = this.tagsListWithAssetCount.filter((tag) => !this.selectedTagsList.some((selectedTag) => selectedTag._id === tag._id));
    }
    this.tempFilteredTagsList = this.tagsListWithAssetCount;
    this.paginatorEvent.emit({ selectedTagsList: this.filteredTagsList, disableApply: disableApply });
  }

  /**
   * update selectedTagsList assetCount when tags get selected by the users
   */
  updateSelectedTagsAssetCount() {
    this.selectedTagsList.forEach((list) => {
      const foundIndex = this.tagsListWithAssetCount.findIndex((tagList) => tagList._id === list._id);
      if (foundIndex > -1) {
        list.assetsCount = this.tagsListWithAssetCount[foundIndex].assetsCount;
      }
    });
  }

  /**
   * Filter tags based on the search filter
   * @param event
   */
  searchTextChanged() {
    if (this.searchText) {
      this.filteredTagsList = this.tagsListWithAssetCount.filter((tags) => tags.name.toLowerCase().includes(this.searchText.toLowerCase()));
    } else {
      this.filteredTagsList = this.tagsListWithAssetCount;
    }
    this.paginatorEvent.emit({ selectedTagsList: this.filteredTagsList });
  }

  /**
   * Change index based on the pagination page number and page size
   * @param index
   * @returns
   */
  paginatedIndex(index: number) {
    return Math.ceil(((this.paginator.pageIndex || 0) + 1 - 1) * (this.paginator.pageSize || 15) + index);
  }

  /**
   * Update tag name and call update api
   * @param index
   */
  saveTagName(tagsId: string) {
    const tagsListIndex = this.tagsListWithAssetCount.findIndex((tag) => tag._id === tagsId);
    const oldTagName = this.tagsListWithAssetCount[tagsListIndex].name;
    this.tagsApiService.tagsControllerUpdate(tagsId, { name: this.tagName }).subscribe({
      next: (res) => {
        if (res) {
          if (tagsListIndex !== -1) this.tagsListWithAssetCount[tagsListIndex].name = this.tagName;
          if (this.selectedTagsList.length) {
            const selectedTagsListIndex = this.selectedTagsList.findIndex((tag) => tag._id === tagsId);
            if (selectedTagsListIndex !== -1) this.selectedTagsList[selectedTagsListIndex].name = this.tagName;
          }
          this.updateTagName.emit({ oldTagName: oldTagName, newTagName: this.tagName });
        }
      },
    });
    this.toggleEditTag = false;
    this.toggleEditSelectedTag = false;
  }

  /**
   * Close rename tag input field
   */
  exitRenameTag() {
    this.toggleEditTag = false;
    this.toggleEditSelectedTag = false;
    this.editingTag = '';
  }

  /**
   * Toggle search tag for renaming it
   * @param index
   */
  renameSelectedTag(index: number) {
    this.toggleEditSelectedTag = true;
    this.toggleEditTag = false;
    const tag = this.selectedTagsList[index];
    // assign the current name to the tabName input
    this.tagName = tag.name;
    this.editingSelectedTag = tag._id;
    setTimeout(() => {
      this.renameSearchInput?.nativeElement?.focus();
      this.renameSearchInput?.nativeElement?.select();
    }, 0);
  }

  /**
   * Toggle tag for renaming it
   * @param index
   */
  renameTag(index: number) {
    const paginatedIndex = this.paginatedIndex(index);
    this.toggleEditTag = true;
    this.toggleEditSelectedTag = false;
    const tag = this.filteredTagsList[paginatedIndex];
    // assign the current name to the tabName input
    this.tagName = tag.name;
    this.editingTag = tag._id;
    setTimeout(() => {
      this.renameInput?.nativeElement?.focus();
      this.renameInput?.nativeElement?.select();
    }, 0);
  }

  /**
   * Remove selected tag from searchTagsList on mat chip click event
   * @param index
   */
  removeSelectedTags(tagName: string) {
    const index = this.selectedTagsList.findIndex((tag) => tag.name === tagName);
    if (index > -1) {
      this.selectedTagsList.splice(index, 1);
    }
    let fetchDataFlag = false;
    let disableApplyFlag = false;
    if (this.tagData && this.tagData.callFrom == 'dashboard') {
      fetchDataFlag = true;
      if (this.selectedTagsList.length == 0) {
        disableApplyFlag = true;
      }
    }
    this.updateTagsList(fetchDataFlag, disableApplyFlag);

    this.searchedTagsEvent.emit({ selectedTagsList: this.selectedTagsList, disableApply: disableApplyFlag });
  }

  /**
   * Add selected tag to searchTagsList on mat chip click event
   * @param event
   */
  addSelectedTags(event: TagsWithAssetCount) {
    const tagExist = this.selectedTagsList.some((tag) => tag._id === event._id);
    if (!tagExist) {
      this.selectedTagsList?.push(event);
      let fetchDataFlag = false;
      if (this.tagData && this.tagData.callFrom == 'dashboard') {
        fetchDataFlag = true;
      }
      this.updateTagsList(fetchDataFlag, false);
    }
    const selectedTag = this.env.feature?.tagGroups === true ? event : event.name;
    this.searchedTagsEvent.emit({ selectedTagsList: this.selectedTagsList, disableApply: false, selectedTagName: selectedTag });
  }

  /**
   * update tags list and filters when user select tag
   */
  updateTagsList(fetchDataFlag?: boolean, disableApply?: boolean) {
    if (this.selectedTagsList.length && this.tagData.callFrom != 'dashboard') {
      this.tagData.filters = {
        ...this.tagData?.filters,
        tags: { $all: this.selectedTagsList.map((list) => list._id) },
      };
    } else {
      delete this.tagData.filters.tags;
    }
    if (!this.tagData || this.tagData.callFrom != 'dashboard' || !fetchDataFlag) {
      this._showLoader();
      this.fetchTagsWithAssetCount(disableApply);
    } else {
      this.updateSelectedTagsList(disableApply);
    }
  }

  updateSelectedTagsList(disableApply?: boolean) {
    // remove selected tags from filteredTagsList
    this.filteredTagsList = this.tempFilteredTagsList.filter((tag) => !this.selectedTagsList.some((selectedTag) => selectedTag._id === tag._id));
    this.tagsListWithAssetCount = this.filteredTagsList;
    this.showEmptyState = this.filteredTagsList.length === 0;
    this.paginatorEvent.emit({ selectedTagsList: this.filteredTagsList, disableApply: disableApply });
  }

  updateSelectedTagsOnCheckboxChanges() {
    // remove selected tags from filteredTagsList
    if (this.selectDefaultKeywordFlag) {
      this.selectedTagsList = this.tempFilteredTagsList.slice(0, 20);
    } else {
      this.selectedTagsList = this.tagData?.selectedTags || [];
    }
    this.filteredTagsList = this.tempFilteredTagsList.filter((tag) => !this.selectedTagsList.some((selectedTag) => selectedTag._id === tag._id));
    this.tagsListWithAssetCount = this.filteredTagsList;
    this.showEmptyState = this.filteredTagsList.length === 0;
    let disableApplyFlag = false;
    if (this.selectedTagsList.length === 0) {
      disableApplyFlag = true;
    }
    this.paginatorEvent.emit({ selectedTagsList: this.filteredTagsList, disableApply: disableApplyFlag, currentlySelectedTagList: this.selectedTagsList });
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }
}
